import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LeadModal from '../components/LeadModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { 
  fetchLeads, 
  createLead, 
  updateLead, 
  deleteLead,
  setFilters,
  setPagination,
  clearError,
  fetchLeadStats
} from '../store/slices/leadsSlice';
import './Leads.css';

const Leads = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const { items: leads, loading, error, pagination, filters, stats } = useSelector(state => state.leads);

  // Local state for modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Constants
  const statuses = ['all', 'New', 'Contacted', 'Qualified', 'Converted', 'Lost'];
  const sources = ['all', 'Website', 'Referral', 'Social Media', 'Email Campaign', 'Trade Show', 'Cold Call', 'Other'];

  // Fetch leads and stats on component mount and when filters change
  useEffect(() => {
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      status: filters.status,
      source: filters.source,
      search: filters.search
    };

    dispatch(fetchLeads(params));
    dispatch(fetchLeadStats());
  }, [dispatch, pagination.page, pagination.limit, filters.status, filters.source, filters.search]);



  const handleAddLead = () => {
    setEditingLead(null);
    setIsModalOpen(true);
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setIsModalOpen(true);
  };

  const handleDeleteLead = (lead) => {
    setLeadToDelete(lead);
    setIsDeleteModalOpen(true);
  };

  const handleSaveLead = async (formData) => {
    setIsSaving(true);
    try {
      if (editingLead) {
        // Update existing lead
        await dispatch(updateLead({ 
          id: editingLead._id, 
          data: formData 
        })).unwrap();
      } else {
        // Create new lead
        await dispatch(createLead(formData)).unwrap();
      }
      setIsModalOpen(false);
      setEditingLead(null);
      // Reset to first page
      dispatch(setPagination({ page: 1 }));
    } catch (err) {
      console.error('Save lead error:', err);
      // Error is handled in Redux state
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (leadToDelete) {
      setIsDeleting(true);
      try {
        await dispatch(deleteLead(leadToDelete._id)).unwrap();
        setIsDeleteModalOpen(false);
        setLeadToDelete(null);
        // Reset to first page if needed
        if (leads.length === 1 && pagination.page > 1) {
          dispatch(setPagination({ page: pagination.page - 1 }));
        }
      } catch (err) {
        console.error('Delete lead error:', err);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleSearchChange = (e) => {
    dispatch(setFilters({ search: e.target.value }));
    dispatch(setPagination({ page: 1 }));
  };

  const handleStatusChange = (e) => {
    dispatch(setFilters({ status: e.target.value }));
    dispatch(setPagination({ page: 1 }));
  };

  const handleSourceChange = (e) => {
    dispatch(setFilters({ source: e.target.value }));
    dispatch(setPagination({ page: 1 }));
  };

  const handlePageChange = (newPage) => {
    dispatch(setPagination({ page: newPage }));
  };

  const handleConvertToCustomer = (lead) => {
    // In a real app, this would convert lead to customer
    // For now, just update status to Converted
    dispatch(updateLead({
      id: lead._id,
      data: {
        status: 'Converted',
        convertedAt: new Date().toISOString()
      }
    })).catch(err => console.error('Convert lead error:', err));
  };

  const handleContactLead = (lead) => {
    // Navigate to conversations or create new conversation
    navigate(`/conversations/${lead._id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'New': { color: '#3b82f6', bg: '#dbeafe' },
      'Contacted': { color: '#f59e0b', bg: '#fef3c7' },
      'Qualified': { color: '#8b5cf6', bg: '#ede9fe' },
      'Converted': { color: '#10b981', bg: '#d1fae5' },
      'Lost': { color: '#ef4444', bg: '#fee2e2' }
    };

    const config = statusConfig[status] || statusConfig['New'];
    return (
      <span 
        className="status-badge" 
        style={{ 
          background: config.bg, 
          color: config.color 
        }}
      >
        {status}
      </span>
    );
  };

  const getSourceBadge = (source) => {
    return (
      <span className="source-badge">
        {source}
      </span>
    );
  };

  // Calculate pagination
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="leads-container">
      {/* Error message display */}
      {error && (
        <div className="alert alert-error">
          <span>{typeof error === 'string' ? error : error.message || 'An error occurred'}</span>
          <button 
            className="alert-close"
            onClick={() => dispatch(clearError())}
          >
            ×
          </button>
        </div>
      )}

      <div className="leads-header">
        <div className="header-content">
          <h1>Leads</h1>
          <p>Manage your sales leads and prospects</p>
        </div>
        <button 
          className="btn-add-lead" 
          onClick={handleAddLead}
          disabled={loading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Lead
        </button>
      </div>

      <div className="leads-toolbar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search leads by name, email, phone, or company..."
            value={filters.search}
            onChange={handleSearchChange}
            className="search-input"
            disabled={loading}
          />
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={handleStatusChange}
            className="filter-select"
            disabled={loading}
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Status' : status}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Source:</label>
          <select
            value={filters.source}
            onChange={handleSourceChange}
            className="filter-select"
            disabled={loading}
          >
            {sources.map(source => (
              <option key={source} value={source}>
                {source === 'all' ? 'All Sources' : source}
              </option>
            ))}
          </select>
        </div>

        <div className="leads-stats">
          <span className="stat-item">
            Total: <strong>{stats.total || pagination.total}</strong>
          </span>
          <span className="stat-item">
            New: <strong>{stats.byStatus?.New || 0}</strong>
          </span>
          <span className="stat-item">
            Qualified: <strong>{stats.byStatus?.Qualified || 0}</strong>
          </span>
          <span className="stat-item">
            Converted: <strong>{stats.byStatus?.Converted || 0}</strong>
          </span>
        </div>
      </div>

      {/* Loading state */}
      {loading && leads.length === 0 && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading leads...</p>
        </div>
      )}

      <div className="leads-list">
        {!loading && leads.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No leads found</h3>
            <p>Try adjusting your search or filter criteria, or add a new lead</p>
            <button className="btn-add-first" onClick={handleAddLead}>
              Add Your First Lead
            </button>
          </div>
        ) : (
          leads.map(lead => (
            <div key={lead._id} className="lead-card">
              <div className="lead-header">
                <div className="lead-title-section">
                  <div className="lead-avatar">
                    {lead.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="lead-info">
                    <h3 className="lead-name">{lead.name}</h3>
                    <p className="lead-company">{lead.company}</p>
                  </div>
                </div>
                <div className="lead-status-group">
                  {getStatusBadge(lead.status)}
                  {getSourceBadge(lead.source)}
                </div>
              </div>

              <div className="lead-details">
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-icon">📧</span>
                    <span className="detail-value">{lead.email}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-icon">📞</span>
                    <span className="detail-value">{lead.phone}</span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Lead Value:</span>
                    <span className="detail-value price">{formatCurrency(lead.value)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Assigned To:</span>
                    <span className="detail-value">{lead.assignedTo || 'Unassigned'}</span>
                  </div>
                </div>
                {lead.notes && (
                  <div className="lead-notes">
                    <span className="notes-label">Notes:</span>
                    <p>{lead.notes}</p>
                  </div>
                )}
                <div className="lead-dates">
                  <div className="date-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span>Created: {formatDate(lead.createdAt)}</span>
                  </div>
                  {lead.lastContacted && (
                    <div className="date-item">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                      <span>Last Contacted: {formatDate(lead.lastContacted)}</span>
                    </div>
                  )}
                  {lead.convertedAt && (
                    <div className="date-item converted">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <span>Converted: {formatDate(lead.convertedAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="lead-actions">
                {lead.status !== 'Converted' && (
                  <button
                    className="action-btn convert-btn"
                    onClick={() => handleConvertToCustomer(lead)}
                    title="Convert to Customer"
                    disabled={loading}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Convert
                  </button>
                )}
                <button
                  className="action-btn contact-btn"
                  onClick={() => handleContactLead(lead)}
                  title="Contact Lead"
                  disabled={loading}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  Contact
                </button>
                <button
                  className="action-btn edit-btn"
                  onClick={() => handleEditLead(lead)}
                  title="Edit Lead"
                  disabled={loading}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
                <button
                  className="action-btn delete-btn"
                  onClick={() => handleDeleteLead(lead)}
                  title="Delete Lead"
                  disabled={loading}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {!loading && leads.length > 0 && totalPages > 1 && (
        <div className="pagination">
          <button 
            className="pagination-btn"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            ← Previous
          </button>
          <div className="pagination-info">
            Page {pagination.page} of {totalPages} ({pagination.total} total leads)
          </div>
          <button 
            className="pagination-btn"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === totalPages}
          >
            Next →
          </button>
        </div>
      )}

      {/* Lead Modal */}
      <LeadModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingLead(null);
        }}
        onSave={handleSaveLead}
        lead={editingLead}
        isLoading={isSaving}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setLeadToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={leadToDelete?.name || ''}
        type="lead"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Leads;



